import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DashboardHourlyBucket,
  DashboardResponse,
} from '../../types';
import {
  getClassDashboardMetrics,
  refreshClassMetrics,
} from '../../services/api';
import DateRangeControl, { DateRange, rangeForDays } from './DateRangeControl';

interface ClassAnalyticsProps {
  school: string;
  grade: number;
  section: string | null;
}

const formatNumber = (value: number | null | undefined, options?: Intl.NumberFormatOptions) => {
  if (value === null || value === undefined) {
    return '—';
  }
  return new Intl.NumberFormat(undefined, options).format(value);
};

const formatMinutes = (value: number | null | undefined) =>
  formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const formatPercent = (value: number | null | undefined) =>
  value === null || value === undefined
    ? '—'
    : `${formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const IST_TIMEZONE = 'Asia/Kolkata';

const formatDayLabel = (isoDate: string) =>
  new Date(isoDate).toLocaleDateString([], { month: 'short', day: 'numeric' });

// --- Redesign helpers ---------------------------------------------------------

interface DailyPoint {
  day: string;
  value: number;
}

// Small area-line chart for "Messages per day". Modeled on ActiveStudentsLineChart.
const MessagesPerDayChart: React.FC<{ points: DailyPoint[] }> = ({ points }) => {
  const height = 200;
  const width = 680;
  const padding = { top: 20, right: 12, bottom: 26, left: 12 };
  const xSpan = width - padding.left - padding.right;
  const ySpan = height - padding.top - padding.bottom;
  const maxValue = points.reduce((acc, p) => Math.max(acc, p.value), 0);
  const safeMax = Math.max(maxValue, 1);
  const baselineY = height - padding.bottom;
  const midY = padding.top + ySpan / 2;

  const coords = points.map((p, index) => {
    const ratio = points.length > 1 ? index / (points.length - 1) : 0.5;
    const x = padding.left + ratio * xSpan;
    const y = padding.top + (1 - p.value / safeMax) * ySpan;
    return { x, y, ...p };
  });

  const linePath = coords.length
    ? coords.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ')
    : '';
  const areaPath = coords.length
    ? `${coords.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ')} L ${coords[coords.length - 1].x},${baselineY} L ${coords[0].x},${baselineY} Z`
    : '';

  const last = coords[coords.length - 1];
  const tickCount = Math.min(4, coords.length);
  const tickInterval = tickCount > 1 ? Math.max(1, Math.floor((coords.length - 1) / (tickCount - 1))) : 1;
  const ticks = coords.filter((_, idx) => idx % tickInterval === 0 || idx === coords.length - 1);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="block h-auto w-full"
      role="img"
      aria-label="Messages per day"
    >
      <defs>
        <linearGradient id="messagesArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1={padding.left} y1={baselineY} x2={width - padding.right} y2={baselineY} stroke="#E9E6F4" strokeWidth={1} />
      <line x1={padding.left} y1={midY} x2={width - padding.right} y2={midY} stroke="#F1EEF9" strokeWidth={1} />
      {areaPath && <path d={areaPath} fill="url(#messagesArea)" />}
      {linePath && (
        <path d={linePath} fill="none" stroke="#7C3AED" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
      )}
      {last && (
        <>
          <circle cx={last.x} cy={last.y} r={9} fill="#7C3AED" opacity={0.18} />
          <circle cx={last.x} cy={last.y} r={5} fill="#7C3AED" />
        </>
      )}
      {ticks.map((t) => (
        <text
          key={`tick-${t.day}`}
          x={t.x}
          y={baselineY + 18}
          fill="#94A3B8"
          fontSize={11}
          textAnchor={t === ticks[0] ? 'start' : t === ticks[ticks.length - 1] ? 'end' : 'middle'}
        >
          {formatDayLabel(t.day)}
        </text>
      ))}
    </svg>
  );
};

type HourBucketKey = 'morning' | 'school' | 'afterSchool' | 'night';

interface HourBucket {
  key: HourBucketKey;
  label: string;
  range: string;
  value: number;
}

// Bucket hourly_activity by IST hour-of-day.
// Morning 6–11, School 12–14, After school 15–19, Night 20–5.
const bucketHours = (buckets: DashboardHourlyBucket[]): HourBucket[] => {
  const totals: Record<HourBucketKey, number> = { morning: 0, school: 0, afterSchool: 0, night: 0 };
  buckets.forEach((bucket) => {
    const hourStr = new Date(bucket.window_start).toLocaleString('en-US', {
      hour: '2-digit',
      hour12: false,
      timeZone: IST_TIMEZONE,
    });
    let hour = parseInt(hourStr, 10);
    if (Number.isNaN(hour)) return;
    if (hour === 24) hour = 0;
    const count = bucket.user_message_count ?? 0;
    if (hour >= 6 && hour <= 11) totals.morning += count;
    else if (hour >= 12 && hour <= 14) totals.school += count;
    else if (hour >= 15 && hour <= 19) totals.afterSchool += count;
    else totals.night += count;
  });
  return [
    { key: 'morning', label: 'Morning', range: '6–12', value: totals.morning },
    { key: 'school', label: 'School', range: '12–3', value: totals.school },
    { key: 'afterSchool', label: 'After school', range: '3–8', value: totals.afterSchool },
    { key: 'night', label: 'Night', range: '8–6', value: totals.night },
  ];
};

const ClassAnalytics: React.FC<ClassAnalyticsProps> = ({ school, grade, section }) => {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [range, setRange] = useState<DateRange>(() => rangeForDays(90));

  const hasClassContext = Boolean(school && grade);

  const handleStudentSnapshotClick = useCallback(
    (studentId: number) => {
      if (!school || !grade) {
        return;
      }
      const params = new URLSearchParams({
        student_id: String(studentId),
        school,
        grade: String(grade),
      });
      if (section) {
        params.set('section', section);
      }
      const url = `/class-conversation?${params.toString()}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [school, grade, section]
  );

  useEffect(() => {
    if (!hasClassContext || !school || !grade) {
      setError('Class details are missing. Please return to the Teacher View and select a class.');
      return;
    }

    const fetchMetrics = async () => {
      try {
        setIsLoading(true);
        setRefreshError(null);
        setIsRefreshing(true);
        try {
          await refreshClassMetrics(school, grade, section ?? undefined, true);
        } catch (refreshErr: any) {
          const message = refreshErr?.message || 'Failed to refresh metrics. Showing last computed data.';
          setRefreshError(message);
        } finally {
          setIsRefreshing(false);
        }

        const response = await getClassDashboardMetrics(school, grade, section, range.start, range.end);
        setData(response);
      } catch (err: any) {
        setError(err?.message || 'Failed to load dashboard metrics.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetrics();
  }, [hasClassContext, school, grade, section, range.start, range.end]);

  const summary = data?.class_summary ?? null;

  // Daily points for the "Messages per day" hero chart (chronological order).
  const dailyPoints = useMemo<DailyPoint[]>(() => {
    const recent = data?.recent_days ?? [];
    return recent
      .filter((d) => d.day)
      .slice()
      .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime())
      .map((d) => ({ day: d.day, value: d.total_user_messages ?? 0 }));
  }, [data]);

  // Busiest day by user messages.
  const busiestDay = useMemo(() => {
    const recent = data?.recent_days ?? [];
    let best: { day: string; count: number } | null = null;
    recent.forEach((d) => {
      const count = d.total_user_messages ?? 0;
      if (d.day && count > 0 && (!best || count > best.count)) {
        best = { day: d.day, count };
      }
    });
    return best as { day: string; count: number } | null;
  }, [data]);

  // Leaderboard: snapshots sorted by user messages desc, capped at 12.
  const leaderboard = useMemo(() => {
    const snapshots = data?.student_snapshots ?? [];
    return snapshots
      .slice()
      .sort((a, b) => (b.total_user_messages ?? 0) - (a.total_user_messages ?? 0))
      .slice(0, 12);
  }, [data]);
  const maxLeaderboardMsgs = useMemo(
    () => leaderboard.reduce((acc, s) => Math.max(acc, s.total_user_messages ?? 0), 0),
    [leaderboard]
  );

  // Hour-of-day buckets for "When they learn".
  const hourBuckets = useMemo(() => bucketHours(data?.hourly_activity ?? []), [data]);
  const maxBucketValue = useMemo(
    () => hourBuckets.reduce((acc, b) => Math.max(acc, b.value), 0),
    [hourBuckets]
  );
  const topBucket = useMemo(() => {
    if (maxBucketValue <= 0) return null;
    return hourBuckets.reduce((best, b) => (b.value > best.value ? b : best), hourBuckets[0]);
  }, [hourBuckets, maxBucketValue]);

  // ONE plain-English sentence built from real data; clauses with null values omitted.
  const insightSentence = useMemo(() => {
    const parts: React.ReactNode[] = [];
    const conversations = summary?.total_conversations ?? null;
    const minutes = summary?.total_minutes ?? null;
    const afterPct = summary?.after_school_user_pct ?? null;

    const hasActivity =
      (conversations ?? 0) > 0 || (minutes ?? 0) > 0 || dailyPoints.some((p) => p.value > 0);
    if (!hasActivity) {
      return null;
    }

    const clauses: React.ReactNode[] = [];
    if (conversations != null) {
      clauses.push(
        <React.Fragment key="chats">
          <b className="font-extrabold text-[#7C3AED]">{formatNumber(conversations)} chats</b>
        </React.Fragment>
      );
    }
    if (minutes != null) {
      clauses.push(
        <React.Fragment key="minutes">
          <b className="font-extrabold text-[#7C3AED]">{formatMinutes(minutes)} minutes</b>
        </React.Fragment>
      );
    }

    if (clauses.length > 0) {
      parts.push(<React.Fragment key="lead">Over this window your class had </React.Fragment>);
      clauses.forEach((clause, idx) => {
        if (idx > 0) parts.push(<React.Fragment key={`sep-${idx}`}> and </React.Fragment>);
        parts.push(clause);
      });
      parts.push(<React.Fragment key="coach"> with the curiosity</React.Fragment>);
      if (afterPct != null) {
        parts.push(
          <React.Fragment key="after">
            {' — '}
            <b className="font-extrabold text-[#10B981]">{formatPercent(afterPct)} of it after school</b>
          </React.Fragment>
        );
      }
      parts.push(<React.Fragment key="dot1">. </React.Fragment>);
    }

    if (busiestDay) {
      parts.push(
        <React.Fragment key="busy">
          Busiest day was <b className="font-extrabold text-[#0F172A]">{formatDayLabel(busiestDay.day)}</b>.
        </React.Fragment>
      );
    }

    return parts.length > 0 ? parts : null;
  }, [summary, dailyPoints, busiestDay]);

  const minutesPerChat = useMemo(() => {
    const conversations = summary?.total_conversations ?? 0;
    const minutes = summary?.total_minutes ?? 0;
    if (conversations > 0) {
      return Math.round(minutes / conversations);
    }
    return null;
  }, [summary]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
          Analytics window
        </p>
        <DateRangeControl value={range} onChange={setRange} />
      </div>

      {isRefreshing && (
        <div className="rounded-2xl border border-violet-200 bg-violet-50 p-4 text-sm text-violet-700">
          Refreshing class metrics…
        </div>
      )}

      {refreshError && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
          {refreshError}
        </div>
      )}

      {isLoading && (
        <div className="rounded-2xl border border-[#E9E6F4] bg-white p-8 text-center text-sm text-slate-600">
          Loading dashboard metrics...
        </div>
      )}

      {error && !isLoading && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          {error}
        </div>
      )}

      {!isLoading && !error && data && (
        <>
          {/* 2 · Insight banner */}
          <section className="flex items-start gap-4 rounded-2xl border border-[#E9E6F4] bg-gradient-to-br from-[#F6F2FF] to-white p-5 shadow-sm">
            <span className="grid h-[42px] w-[42px] flex-none place-items-center rounded-xl bg-[#7C3AED] text-xl text-white">
              ✦
            </span>
            <p className="m-0 text-[17px] font-semibold leading-relaxed text-[#0F172A]">
              {insightSentence ?? 'No curiosity activity in this window yet.'}
            </p>
          </section>

          {/* 3 · Metric tiles */}
          <section className="grid gap-4 md:grid-cols-3">
            <article className="rounded-2xl border border-[#E9E6F4] bg-white p-5 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-[0.06em] text-[#64748B]">Conversations</p>
              <p className="mt-1.5 text-[40px] font-bold leading-none tracking-tight tabular-nums text-[#0F172A]">
                {formatNumber(summary?.total_conversations)}
              </p>
              <p className="mt-1 text-[12.5px] text-[#64748B]">chats started</p>
            </article>
            <article className="rounded-2xl border border-[#E9E6F4] bg-white p-5 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-[0.06em] text-[#64748B]">Time with curiosity</p>
              <p className="mt-1.5 text-[40px] font-bold leading-none tracking-tight tabular-nums text-[#0F172A]">
                {formatMinutes(summary?.total_minutes)}
                <span className="text-lg font-bold text-[#64748B]"> min</span>
              </p>
              <p className="mt-1 text-[12.5px] text-[#64748B]">
                {minutesPerChat != null ? `≈ ${minutesPerChat} min per chat` : 'across the window'}
              </p>
            </article>
            <article className="rounded-2xl border border-[#E9E6F4] bg-white p-5 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-[0.06em] text-[#64748B]">After school</p>
              <p className="mt-1.5 text-[40px] font-bold leading-none tracking-tight tabular-nums text-[#0F172A]">
                {summary?.after_school_user_pct != null
                  ? formatNumber(summary.after_school_user_pct, { maximumFractionDigits: 0 })
                  : '—'}
                {summary?.after_school_user_pct != null && <span className="text-xl">%</span>}
              </p>
              <p className="mt-1 text-[12.5px] text-[#64748B]">of messages outside class hours</p>
            </article>
          </section>

          {/* 4 · Messages per day hero */}
          <section className="rounded-2xl border border-[#E9E6F4] bg-white p-6 shadow-sm">
            <div className="mb-1 flex items-end justify-between gap-3">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Activity</span>
                <h2 className="mt-0.5 text-[17px] font-bold tracking-tight text-[#0F172A]">Messages per day</h2>
              </div>
            </div>
            {dailyPoints.length === 0 || dailyPoints.every((p) => p.value === 0) ? (
              <p className="mt-3 text-sm text-slate-500">No day-level activity in this window yet.</p>
            ) : (
              <div className="mt-2">
                <MessagesPerDayChart points={dailyPoints} />
              </div>
            )}
          </section>

          {/* 5 · When they learn */}
          <section className="rounded-2xl border border-[#E9E6F4] bg-white p-6 shadow-sm">
            <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">When they learn</span>
            <h2 className="mt-0.5 text-[17px] font-bold tracking-tight text-[#0F172A]">
              {topBucket ? `Most active ${topBucket.label.toLowerCase()}` : 'When they learn'}
            </h2>
            {maxBucketValue <= 0 ? (
              <p className="mt-3 text-sm text-slate-500">No hourly activity recorded.</p>
            ) : (
              <div className="mt-4 grid grid-cols-4 items-end gap-3">
                {hourBuckets.map((bucket) => {
                  const heightPct = Math.round((bucket.value / maxBucketValue) * 100);
                  const isTop = bucket.key === topBucket?.key;
                  return (
                    <div key={bucket.key} className="flex flex-col items-center gap-2">
                      <div className="flex h-24 w-full items-end">
                        <div
                          className="w-full rounded-t-lg"
                          style={{
                            height: `${Math.max(heightPct, bucket.value > 0 ? 4 : 0)}%`,
                            background: isTop
                              ? 'linear-gradient(180deg,#A78BFA,#7C3AED)'
                              : '#E4DEF6',
                          }}
                          title={`${bucket.label}: ${formatNumber(bucket.value)} messages`}
                        />
                      </div>
                      <span className="text-xs font-bold text-[#475569]">{bucket.label}</span>
                      <span className="text-[11px] text-slate-400">{bucket.range}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* 6 · Students leaderboard */}
          <section className="rounded-2xl border border-[#E9E6F4] bg-white p-6 shadow-sm">
            <div className="flex items-end justify-between gap-3">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Students</span>
                <h2 className="mt-0.5 text-[17px] font-bold tracking-tight text-[#0F172A]">Who&rsquo;s putting in the time</h2>
              </div>
            </div>
            {leaderboard.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">No student activity yet for this class.</p>
            ) : (
              <div className="mt-3 flex flex-col">
                {leaderboard.map((snapshot, index) => {
                  const msgs = snapshot.total_user_messages ?? 0;
                  const widthPct = maxLeaderboardMsgs > 0 ? Math.round((msgs / maxLeaderboardMsgs) * 100) : 0;
                  return (
                    <div
                      key={snapshot.student_id}
                      className="grid grid-cols-[26px_1fr_92px] items-center gap-3 border-t border-[#F1EEF9] py-3 first:border-t-0 sm:grid-cols-[26px_1fr_150px_92px] sm:gap-4"
                    >
                      <span className="text-[13px] font-bold text-slate-400">{index + 1}</span>
                      <button
                        type="button"
                        className="text-left text-[14.5px] font-bold text-[#0F172A] hover:text-[#7C3AED] hover:underline hover:underline-offset-4"
                        onClick={() => handleStudentSnapshotClick(snapshot.student_id)}
                      >
                        {snapshot.student_name ?? `Student ${snapshot.student_id}`}
                      </button>
                      <div className="hidden h-2 overflow-hidden rounded-full bg-[#F1EEF9] sm:block">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${widthPct}%`,
                            background: 'linear-gradient(90deg,#A78BFA,#7C3AED)',
                          }}
                        />
                      </div>
                      <span className="text-right text-[13px] font-bold text-[#0F172A] tabular-nums">
                        {formatNumber(msgs)}
                        <small className="block text-[11px] font-semibold text-slate-400">messages</small>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

        </>
      )}
    </div>
  );
};

export default ClassAnalytics;
